import React, { useState, useContext, useEffect } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import DotContext from "../contexts/DotContext";
import { useSessionTheme } from "../contexts/SessionThemeContext";
import { SOUND_OPTIONS } from "../utils/soundOptions";
import "./styles/Sidebar.css";

const Sidebar = ({
  isVisible,
  closeSidebar,
  dotSize,
  setDotSize,
  dotSpeed,
  setDotSpeed,
  setExitSession,
  hideExitButton = false,
}) => {
  const {
    soundFile,
    setSoundFile,
    setMute,
    volume,
    setVolume,
    sessionCuesEnabled,
    setSessionCuesEnabled,
    dotBrightness,
    setDotBrightness,
  } = useContext(DotContext);
  const { theme, setTheme, effectiveTheme } = useSessionTheme();
  const [selectedSound, setSelectedSound] = useState(soundFile);
  const [isAdjustingSpeed, setIsAdjustingSpeed] = useState(false);
  const [isAdjustingSize, setIsAdjustingSize] = useState(false);

  useEffect(() => {
    if (isAdjustingSpeed) {
      setMute(true);
      const timer = setTimeout(() => {
        setMute(false);
        setIsAdjustingSpeed(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAdjustingSpeed, setMute]);

  useEffect(() => {
    if (isAdjustingSize) {
      setMute(true);
      const timer = setTimeout(() => {
        setMute(false);
        setIsAdjustingSize(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAdjustingSize, setMute]);

  const handleSoundChange = (e) => {
    const newSound = e.target.value;
    setSelectedSound(newSound);
    setSoundFile(newSound);

    const audio = new Audio(newSound);
    audio.volume = volume;
    audio.play().catch((error) => {
      console.error("Error playing sound:", error);
    });
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const maxSpeedValue = 2500;
  const minSpeedValue = 500;

  const handleSpeedChange = (e) => {
    const sliderValue = parseFloat(e.target.value);
    const sliderMin = 50;
    const sliderMax = 200;
    const newSpeedValue = maxSpeedValue - (sliderValue - sliderMin) * (maxSpeedValue - minSpeedValue) / (sliderMax - sliderMin);
    setIsAdjustingSpeed(true);
    setDotSpeed(newSpeedValue);
  };

  const handleExitSession = () => {
    setExitSession(true);
    closeSidebar();
  };

  const themeOptions = [
    { id: "dark",   label: "Dark",   Icon: Moon },
    { id: "light",  label: "Light",  Icon: Sun },
    { id: "system", label: "System", Icon: Monitor },
  ];

  return (
    <div
      data-session-theme={effectiveTheme}
      className={`fixed top-0 left-0 h-full w-full md:w-72 landscape:w-72 bg-session-bg text-session-text shadow-lg transition-transform duration-300 z-50 transform ${
        isVisible ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <button
        onClick={closeSidebar}
        className="absolute top-2 right-2 md:top-0 md:right-0 p-3 md:p-2 landscape:p-1 text-session-text text-3xl md:text-2xl landscape:text-2xl bg-transparent hover:bg-session-bg-elevated rounded-full transition-colors z-10"
        aria-label="Close sidebar"
      >
        &times;
      </button>

      <div className="p-4 md:p-4 landscape:p-2 landscape:pt-8 overflow-y-auto h-full">
        <div className="mb-4">
          <p className="font-semibold mb-2">Theme</p>
          <div role="radiogroup" aria-label="Session theme" className="grid grid-cols-3 gap-1 rounded-lg border border-session-text-secondary/20 p-1">
            {themeOptions.map(({ id, label, Icon }) => {
              const active = theme === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setTheme(id)}
                  className={`flex flex-col items-center justify-center gap-1 py-2 rounded-md text-xs transition ${
                    active
                      ? "bg-session-accent text-session-bg"
                      : "text-session-text-secondary hover:bg-session-bg-elevated hover:text-session-text"
                  }`}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <p className="font-semibold">Dot brightness: {Math.round(dotBrightness * 100)}%</p>
          <input
            type="range"
            min="30"
            max="100"
            step="5"
            value={Math.round(dotBrightness * 100)}
            onChange={(e) => setDotBrightness(parseFloat(e.target.value) / 100)}
            className="w-full accent-session-accent"
          />
        </div>

        <div className="mb-4">
          <p className="font-semibold">Dot Size:</p>
          <input
            type="range"
            min="10"
            max="100"
            value={dotSize}
            onChange={(e) => {
              setDotSize(e.target.value);
              setIsAdjustingSize(true);
            }}
            className="w-full accent-session-accent"
          />
        </div>

        <div className="mb-4">
          <p className="font-semibold">Dot Speed:</p>
          <input
            type="range"
            min={50}
            max={200}
            step="1"
            value={200 - (dotSpeed - 500) * (200 - 50) / (2500 - 500)}
            onChange={handleSpeedChange}
            className="w-full accent-session-accent"
          />
        </div>

        <div className="mb-4">
          <p className="font-semibold">Select Sound:</p>
          <select
            value={selectedSound}
            onChange={handleSoundChange}
            className="w-full p-2 bg-session-bg-elevated text-session-text border border-session-text-secondary/20 rounded"
          >
            {SOUND_OPTIONS.map((s) => (
              <option key={s.file} value={s.file}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <p className="font-semibold">Volume: {Math.round(volume * 100)}%</p>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={handleVolumeChange}
            className="w-full accent-session-accent"
          />
        </div>

        <div className="mb-4">
          <p className="font-semibold">Show session cues:</p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sessionCuesEnabled}
              onChange={(e) => setSessionCuesEnabled(e.target.checked)}
              className="h-4 w-4 accent-session-accent"
            />
            <span className="text-sm text-session-text-secondary">Short cue phrase during dot movement</span>
          </label>
        </div>

        {!hideExitButton && (
          <div className="mt-auto">
            <button
              onClick={handleExitSession}
              className="w-full bg-session-accent text-session-bg font-bold py-2 rounded hover:opacity-90 transition"
            >
              Exit Session
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
